import { useMemo, useState } from "react";
import { useTellescopeQuery, useTellescopeQueryClient } from "@tellescope/tanstack-query";
import { applyMongoFilter, compileFreeformFilterBlock, formatName } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import type { RelatedContactWithType, UseRelatedContactsBlockReturn } from "../../../hooks";

export type UseRelatedContactsListViewArgs = Pick<
  UseRelatedContactsBlockReturn,
  "enduserId" | "relationships" | "filterBlock" | "updateEnduserMutation"
>;

export const useRelatedContactsListView = ({
  enduserId,
  relationships,
  filterBlock,
  updateEnduserMutation,
}: UseRelatedContactsListViewArgs) => {
  const { t } = useTranslation("contacts");
  const queryClient = useTellescopeQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const relationshipIds = useMemo(() => relationships.map((rel) => rel.id), [relationships]);

  // Batch-fetch every related contact in one request.
  const relatedContactsQuery = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: { ids: relationshipIds },
    enabled: relationshipIds.length > 0,
  });

  const relatedContactsWithTypes = useMemo<RelatedContactWithType[]>(() => {
    if (!relatedContactsQuery.data?.matches) return [];
    const contactsById = new Map(relatedContactsQuery.data.matches.map((c) => [c.id, c]));
    return relationships.reduce<RelatedContactWithType[]>((acc, relationship) => {
      const relatedContact = contactsById.get(relationship.id);
      if (relatedContact) acc.push({ contact: relatedContact, relationshipType: relationship.type ?? t("Unknown") });
      return acc;
    }, []);
  }, [relatedContactsQuery.data, relationships, t]);

  // Each contact's relationship type is evaluated individually as a scalar `type` field, so array
  // needles use per-item semantics (equals matches any selected type; does-not-equal excludes them).
  const compiledFilter = useMemo(
    () => compileFreeformFilterBlock(filterBlock, { evalArraysByItem: true }),
    [filterBlock]
  );

  const searchedRelatedContacts = useMemo(() => {
    if (!searchQuery) return relatedContactsWithTypes;
    const query = searchQuery.toLowerCase();
    return relatedContactsWithTypes.filter(
      ({ contact: c, relationshipType }) =>
        formatName(c).toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query) ||
        c.phone?.includes(query) ||
        relationshipType.toLowerCase().includes(query)
    );
  }, [relatedContactsWithTypes, searchQuery]);

  const filteredRelatedContacts = useMemo(() => {
    if (!compiledFilter) return searchedRelatedContacts;
    return searchedRelatedContacts.filter(({ relationshipType }) =>
      applyMongoFilter({ type: relationshipType }, compiledFilter)
    );
  }, [searchedRelatedContacts, compiledFilter]);

  const selectedRelatedContact = useMemo(() => {
    if (!selectedContactId) return null;
    return relatedContactsWithTypes.find(({ contact: c }) => c.id === selectedContactId) ?? null;
  }, [selectedContactId, relatedContactsWithTypes]);

  const removeRelatedContact = () => {
    if (!selectedContactId) return;

    // Read the latest cached records so a concurrent edit isn't stomped when we replace the arrays.
    const latestContact = queryClient.getQueryData({ model: "endusers", method: "getOne", request: enduserId });
    const currentRelationships = latestContact?.relationships ?? relationships;
    const updatedRelationships = currentRelationships.filter((rel) => rel.id !== selectedContactId);

    const relatedContact = queryClient.getQueryData({
      model: "endusers",
      method: "getOne",
      request: selectedContactId,
    });
    const updatedRelatedContactRelationships = (relatedContact?.relationships ?? []).filter(
      (rel) => rel.id !== enduserId
    );

    // Relationships are bidirectional — remove both sides (A→B, then B→A).
    updateEnduserMutation.mutate(
      { id: enduserId, relationships: updatedRelationships, _options: { replaceObjectFields: true } },
      {
        onSuccess: () => {
          updateEnduserMutation.mutate(
            {
              id: selectedContactId,
              relationships: updatedRelatedContactRelationships,
              _options: { replaceObjectFields: true },
            },
            {
              onSuccess: () => {
                setSelectedContactId(null);
                setShowDeleteConfirmation(false);
              },
            }
          );
        },
      }
    );
  };

  return {
    searchQuery,
    setSearchQuery,
    filteredRelatedContacts,
    hasActiveFilter: filterBlock.filters.length > 0,
    hasRelationships: relationshipIds.length > 0,
    isLoading: relatedContactsQuery.isLoading,
    selectedContactId,
    setSelectedContactId,
    selectedRelatedContact,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    removeRelatedContact,
  };
};
